/**
 * 三合一卡片配置（管理后台）
 *
 * 独立配置三合一模式的 LLM 调用参数、扣费限频，以及 3 张固定卡的融合模板。
 */

import { useEffect, useState } from 'react';
import { Loader2, Save } from 'lucide-react';
import toast from '../../Toast';
import {
  directorAdminApi,
  type TrinityCardsConfig,
} from '../../../apps/director/services/directorAdminApi';
import LlmModelPicker from '../llm/LlmModelPicker';

const OFFICIAL_TRINITY_SYSTEM_PROMPT = `你是导演模式的三合一参考卡提示词助手。

你的任务是把用户的一条分镜需求，拆成同一创意源下的 3 条高质量中文视觉提示词，用于生成：
1. 角色卡（role）—— 身份锚点
2. 故事卡（story）—— 同身份在分镜剧情下的连续动作
3. 视频卡（video）—— 同身份在最终视频执行层的视觉总控

依赖关系（严格单向，不可调换）：
- role 不依赖 story / video，是身份基线
- story 必须继承 role 的同一主体身份
- video 必须同时继承 role 的身份与 story 的节拍/镜头秩序

【输出格式·硬约束】
你的整段输出必须严格遵守下面这种纯文本结构。三个分隔符行 \`===ROLE===\` / \`===STORY===\` / \`===VIDEO===\` 必须独占一行，原样输出（不要 markdown 加粗、不要 #、不要被引号或代码块包裹）：

===ROLE===
（这里写完整的角色卡中文视觉提示词，可以是多行）

===STORY===
（这里写完整的故事卡中文视觉提示词，可以是多行）

===VIDEO===
（这里写完整的视频卡中文视觉提示词，可以是多行）

不允许出现的格式：
- 用 \`{}\` 或 JSON 输出
- 用 markdown 标题（如 \`### 角色卡\`、\`# ROLE\`）替代分隔符
- 把分隔符行包在代码块（\`\`\`）里
- 在分隔符两侧添加额外字符（如 \`**===ROLE===**\`、\`> ===ROLE===\`）
- 在三段之外添加任何多余说明、前言、总结、编号

【参考图硬约束规则】
当资产上下文中存在标注"有参考图"的角色时：
- 参考图是绝对硬约束，优先级高于一切风格发挥与构图自由
- 当只有 1 个有参考图的角色时，该角色就是唯一主身份主体，role / story / video 都必须围绕该角色保持身份一致
- 当恰好有 2 个有参考图的角色时，这 2 个角色都是主约束角色：role 卡必须把两人都作为清晰、独立、可辨认的主体呈现；不允许只突出其中 1 人、也不允许把另一人降成背景路人
- 当有 2 个有参考图的角色时，两人的脸、发、服装、体型、独特标记必须分别继承各自参考图，不允许相互混用、拼接、镜像、融合成同一个人
- 当有 3 个及以上有参考图的角色时，优先保证前 2 个角色的身份稳定，其余角色仅可作为辅助同框信息；不要让画面退化成群像海报
- 严禁任何形式的"重设计 / 重新诠释 / 风格化转译"
- 不允许出现"年轻化版本 / 老年版本 / 性别反转 / 种族切换 / 童年版 / 未来版"等任何身份偏移描述
- 不允许把同一角色画成"双胞胎 / 多个分身 / 不同时期"

【强制重申短语】
当只有 1 个有参考图的角色时，role / story / video 三段都必须显式包含一次以下短语（可整合到自然语言中，但必须可识别）：
"主体身份与角色参考图完全一致，脸、发、服装、体型、独特标记逐项继承"
当恰好有 2 个有参考图的角色时，role / story / video 三段都必须显式包含一次以下双角色约束（可整合到自然语言中，但必须可识别）：
"角色1身份与角色1参考图完全一致，角色2身份与角色2参考图完全一致，脸、发、服装、体型、独特标记分别继承，不可混用"
这是为了让下游图片模型在每张卡都拿到同一份身份硬约束。

【其它规则】
- 三段提示词来自同一创意宇宙，互不矛盾
- 角色卡必须服务"身份锁定 + 后续复用"；当有 2 个参考角色时，应生成双角色关系身份卡，而不是单主角海报
- 故事卡必须明确继承角色卡里的同一主体身份，不能把角色画成另一个人
- 视频卡必须同时继承角色卡身份与故事卡叙事/镜头秩序，体现最终成片气质
- 物品/场景参考图作为环境强参考，但不得覆盖人物身份约束
- 输出内容具体、可执行、有镜头感，但保持为单条完整提示词`;

const OFFICIAL_TRINITY_TEMPLATES = {
  role:
    '【身份绝对锁定·硬约束】当存在角色参考图时，优先按角色数量决定画面结构：若只有 1 个角色参考图，则按单角色身份板生成，并显式包含“主体身份与角色参考图完全一致，脸、发、服装、体型、独特标记逐项继承”；若恰好有 2 个角色参考图，则必须生成双角色关系身份卡，并显式包含“角色1身份与角色1参考图完全一致，角色2身份与角色2参考图完全一致，脸、发、服装、体型、独特标记分别继承，不可混用”。注意：不要在提示词中描述任何具体外观特征（发色、服装款式、肤色等），参考图会单独传入图片模型。允许变化的仅有：姿势、镜头角度、表情、关系站位、轻微光影。【画面规格】16:9 艺术性角色身份板，纯白色或柔和米白色背景；无品牌标志、无水印。若为单角色，可无环境、无道具；若为双角色，可只保留极少量辅助关系道具，但不能喧宾夺主。【布局】不要标准网格参考表，做电影感艺术书式身份板：留白充足、布局优雅、比例多样。单角色时：放置一个大型英雄全身视角，围绕它排列 8-10 个独立辅助研究视角。双角色时：必须同时呈现两人，不能只突出其中 1 人；建议采用“1 个双人主构图 + 2 个角色各自独立全身研究视角 + 2-4 个双人互动/站位/姿态研究 + 少量头肩表情研究”的关系设定板结构。所有人物彼此分离不重叠，不裁脸、不藏肢体、不堆叠人物、不把第二角色缩成背景路人、不把两人融合成同一个人。【身份重申·强制】单角色时，每个视角都必须严格基于该角色参考图；双角色时，两个角色都必须清晰、独立、可辨认，并分别继承各自参考图，禁止混用脸、发、服装、体型和独特标记。【辅助研究区·克制】可加入小型轮廓研究（2-3 个简化黑色剪影）、微表情研究（细微情绪变化）；双角色时可加入关系距离、视线方向、肢体张力研究。【文字设计】加入时尚的角色 ID / 关系 ID 块，简约大胆，仅包含名称、角色定位、核心情绪、关系关键词、视觉标志；保持克制优雅。【最终目标】高端动画工作室角色研究 × 艺术书布局结合体；单角色时稳定锁定主体身份，双角色时稳定锁定两人身份与关系，便于后续故事卡与视频卡严格继承。',
  story:
    '请把用户输入融合为一张 16:9 的 12 格电影故事板提示词，并明确继承角色卡里已经锁定的同一主体身份。故事板必须是黑白粗铅笔线稿、轻量手势绘制、未完成但动态强烈的编排预览；每格都要有明显动作、镜头调度、身体动量与情绪推进，避免静态站立；保留最小环境，只保留空间、烟雾、织物、光束、反射等舞台信息；加入红色身体运动箭头、蓝色摄影机运动箭头、绿色构图标记、橙色灯光方向、紫色情绪/声音强调、黑色简短镜头标签，使其成为后续视频卡的节拍蓝图。',
  video:
    '请把用户输入融合为一张最终视频参考卡提示词，并明确同时继承角色卡的主体身份与故事卡的节拍/镜头秩序。输出应像用于 img2video 的高强度视频首帧或视觉总控参考图：保留角色绝对一致的脸、服装、轮廓和气质；吸收故事卡中的动作快照、构图切换、情绪递进与镜头能量；强调电影级质感、节奏爆发力、镜头运动感、最终成片的视觉冲击与统一风格，让它成为最终视频生成的执行层参考。',
};

const DEFAULT_FORM: TrinityCardsConfig = {
  trinityCardsLlmEnabled: false,
  trinityCardsLlmModelId: '',
  trinityCardsLlmSystemPrompt: '',
  trinityCardsCreditsPerCall: 3,
  trinityCardsRateLimitPerMinute: 5,
  trinityCardsPromptTemplates: {
    role: '',
    story: '',
    video: '',
  },
};

export default function DirectorTrinityCardsConfigTab() {
  const [form, setForm] = useState<TrinityCardsConfig>(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    directorAdminApi
      .getTrinityCardsConfig()
      .then((cfg) => setForm(cfg))
      .catch((err) => toast.error(err instanceof Error ? err.message : '加载失败'))
      .finally(() => setLoading(false));
  }, []);

  const update = <K extends keyof TrinityCardsConfig>(
    key: K,
    value: TrinityCardsConfig[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateTemplate = (
    key: keyof TrinityCardsConfig['trinityCardsPromptTemplates'],
    value: string
  ) => {
    setForm((prev) => ({
      ...prev,
      trinityCardsPromptTemplates: {
        ...prev.trinityCardsPromptTemplates,
        [key]: value,
      },
    }));
  };

  const handleSave = async () => {
    const modelId = (form.trinityCardsLlmModelId ?? '').trim();
    if (form.trinityCardsLlmEnabled && modelId && !isUuid(modelId)) {
      toast.error('请重新选择 LLM 模型（当前值非合法模型 ID）');
      return;
    }
    setSaving(true);
    try {
      const patch: Partial<TrinityCardsConfig> = {
        trinityCardsLlmEnabled: form.trinityCardsLlmEnabled,
        trinityCardsLlmModelId: modelId || null,
        trinityCardsLlmSystemPrompt: form.trinityCardsLlmSystemPrompt,
        trinityCardsCreditsPerCall: form.trinityCardsCreditsPerCall,
        trinityCardsRateLimitPerMinute: form.trinityCardsRateLimitPerMinute,
        trinityCardsPromptTemplates: form.trinityCardsPromptTemplates,
      };

      await directorAdminApi.updateTrinityCardsConfig(patch);
      toast.success('已保存');
      const fresh = await directorAdminApi.getTrinityCardsConfig();
      setForm(fresh);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  function isUuid(v: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-light-muted">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        加载中
      </div>
    );
  }

  return (
    <div className="card max-w-4xl space-y-5 p-6">
      <div>
        <h3 className="mb-1 text-lg font-semibold">三合一卡片融合配置</h3>
        <p className="text-sm text-light-muted">
          用户输入一条主需求后，后台会先调用 LLM 融合成角色卡、故事卡、视频卡三条提示词，再分别生成三张参考图。
        </p>
      </div>

      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={form.trinityCardsLlmEnabled}
          onChange={(e) => update('trinityCardsLlmEnabled', e.target.checked)}
        />
        <span className="text-sm font-medium">启用三合一 AI 融合</span>
      </label>

      <div>
        <label className="mb-1 block text-sm font-medium">LLM 模型</label>
        <LlmModelPicker
          value={form.trinityCardsLlmModelId}
          onChange={(id) => update('trinityCardsLlmModelId', id)}
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium">System Prompt（可选）</label>
        <div className="mb-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => update('trinityCardsLlmSystemPrompt', OFFICIAL_TRINITY_SYSTEM_PROMPT)}
            className="btn-secondary px-3 py-1.5 text-sm"
          >
            填入官方 System Prompt
          </button>
        </div>
        <textarea
          value={form.trinityCardsLlmSystemPrompt ?? ''}
          onChange={(e) => update('trinityCardsLlmSystemPrompt', e.target.value)}
          rows={5}
          placeholder="留空则使用内置默认 system prompt"
          className="input w-full text-sm font-mono"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium">单次融合扣费积分</label>
          <input
            type="number"
            min={0}
            value={form.trinityCardsCreditsPerCall}
            onChange={(e) =>
              update('trinityCardsCreditsPerCall', parseInt(e.target.value, 10) || 0)
            }
            className="input w-full text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium">每用户每分钟最多调用次数</label>
          <input
            type="number"
            min={1}
            value={form.trinityCardsRateLimitPerMinute}
            onChange={(e) =>
              update('trinityCardsRateLimitPerMinute', parseInt(e.target.value, 10) || 1)
            }
            className="input w-full text-sm"
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-3">
          <button
            type="button"
            onClick={() =>
              update('trinityCardsPromptTemplates', {
                ...OFFICIAL_TRINITY_TEMPLATES,
              })
            }
            className="btn-secondary px-3 py-1.5 text-sm"
          >
            填入官方三合一模板
          </button>
        </div>
        <TemplateField
          label="角色卡融合提示词"
          value={form.trinityCardsPromptTemplates.role}
          onChange={(value) => updateTemplate('role', value)}
          placeholder="把用户主需求融合成角色卡生成提示词"
        />
        <TemplateField
          label="故事卡融合提示词"
          value={form.trinityCardsPromptTemplates.story}
          onChange={(value) => updateTemplate('story', value)}
          placeholder="把用户主需求融合成故事卡生成提示词"
        />
        <TemplateField
          label="视频卡融合提示词"
          value={form.trinityCardsPromptTemplates.video}
          onChange={(value) => updateTemplate('video', value)}
          placeholder="把用户主需求融合成视频卡生成提示词"
        />
      </div>

      <div className="pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary flex items-center gap-2 px-5 py-2"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          保存配置
        </button>
      </div>
    </div>
  );
}

function TemplateField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={9}
        placeholder={placeholder}
        className="input w-full text-sm font-mono"
      />
    </div>
  );
}
